import { useState, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AdminLayout from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import KnowledgeBaseStats from "@/components/ai/KnowledgeBaseStats";
import BookContextTable from "@/components/ai/BookContextTable";
import BulkActionToolbar, {
  type BulkOperationProgress,
} from "@/components/ai/BulkActionToolbar";
import {
  fetchKnowledgeBaseStats,
  fetchKnowledgeBaseBooks,
  prefillAiContextFromChapters,
  generateEmbeddingsForBook,
  type KnowledgeBaseFilters,
  type KnowledgeBaseBook,
} from "@/api/aiKnowledgeBase";
import { fetchCategories } from "@/api/categories";
import { fetchLanguages } from "@/api/languages";

const PAGE_SIZE = 20;

const defaultFilters: KnowledgeBaseFilters = {
  search: "",
  aiContextStatus: "all",
  embeddingStatus: "all",
  language: "",
  category: "",
};

const defaultProgress: BulkOperationProgress = {
  isRunning: false,
  type: null,
  total: 0,
  completed: 0,
  currentBookTitle: "",
  errors: [],
};

const AiKnowledgeBase = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const cancelRef = useRef(false);

  // State
  const [filters, setFilters] = useState<KnowledgeBaseFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState("title");
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [progress, setProgress] = useState<BulkOperationProgress>(defaultProgress);

  const debouncedSearch = useDebounce(filters.search, 500);
  const effectiveFilters = { ...filters, search: debouncedSearch };

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["knowledge-base-stats"],
    queryFn: fetchKnowledgeBaseStats,
  });

  const { data: booksResult, isLoading: booksLoading } = useQuery({
    queryKey: ["knowledge-base-books", page, PAGE_SIZE, effectiveFilters, sortField, sortAsc],
    queryFn: () => fetchKnowledgeBaseBooks(page, PAGE_SIZE, effectiveFilters, sortField, sortAsc),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: languages = [] } = useQuery({
    queryKey: ["languages"],
    queryFn: fetchLanguages,
  });

  // Handlers
  const handleFiltersChange = (newFilters: KnowledgeBaseFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleSortChange = (field: string) => {
    if (field === sortField) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ["knowledge-base-stats"] });
    queryClient.invalidateQueries({ queryKey: ["knowledge-base-books"] });
  };

  // Get book title by id from current data
  const getBookTitle = useCallback(
    (bookId: number): string => {
      return booksResult?.books.find((b) => b.id === bookId)?.title || `Book #${bookId}`;
    },
    [booksResult]
  );

  // ─── Single-book operations ────────────────────────────────────────────────

  const handleSinglePrefill = async (bookId: number) => {
    const title = getBookTitle(bookId);
    try {
      const result = await prefillAiContextFromChapters(bookId);
      toast({
        title: "AI context pre-filled",
        description: `${result.languagesUpdated} language(s) updated from ${result.totalChapters} chapter(s) for "${title}".`,
      });
      invalidateAll();
    } catch (error) {
      toast({
        title: "Pre-fill failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleSingleGenerate = async (bookId: number) => {
    const title = getBookTitle(bookId);
    try {
      const result = await generateEmbeddingsForBook(bookId);
      toast({
        title: "Embeddings generated",
        description: `${result.chunksProcessed} chunk(s) processed for "${title}".`,
      });
      invalidateAll();
    } catch (error) {
      toast({
        title: "Embedding generation failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const handleSinglePrefillAndGenerate = async (bookId: number) => {
    const title = getBookTitle(bookId);
    try {
      await prefillAiContextFromChapters(bookId);
      const result = await generateEmbeddingsForBook(bookId);
      toast({
        title: "Pre-fill + Generate complete",
        description: `${result.chunksProcessed} chunk(s) embedded for "${title}".`,
      });
      invalidateAll();
    } catch (error) {
      toast({
        title: "Operation failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  // ─── Bulk operations ───────────────────────────────────────────────────────

  // We need all books data to get titles for bulk ops — use a helper to fetch titles for IDs not on current page
  const resolveBookTitles = async (ids: number[]): Promise<Map<number, string>> => {
    const titleMap = new Map<number, string>();
    // Use current page data first
    for (const book of booksResult?.books || []) {
      if (ids.includes(book.id)) {
        titleMap.set(book.id, book.title);
      }
    }
    // For IDs not found on current page, we just use ID as fallback
    for (const id of ids) {
      if (!titleMap.has(id)) titleMap.set(id, `Book #${id}`);
    }
    return titleMap;
  };

  const runBulkPrefill = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    cancelRef.current = false;
    const titles = await resolveBookTitles(ids);
    const errors: BulkOperationProgress["errors"] = [];

    setProgress({
      isRunning: true,
      type: "prefill",
      total: ids.length,
      completed: 0,
      currentBookTitle: "",
      errors: [],
    });

    for (let i = 0; i < ids.length; i++) {
      if (cancelRef.current) break;
      const bookId = ids[i];
      const title = titles.get(bookId) || `Book #${bookId}`;

      setProgress((p) => ({ ...p, currentBookTitle: title, completed: i }));

      try {
        await prefillAiContextFromChapters(bookId);
      } catch (error) {
        errors.push({ bookId, title, error: error instanceof Error ? error.message : "Unknown error" });
      }
    }

    setProgress((p) => ({
      ...p,
      isRunning: false,
      completed: cancelRef.current ? p.completed : ids.length,
      currentBookTitle: "",
      errors,
    }));

    invalidateAll();
    const successCount = ids.length - errors.length;
    toast({
      title: "Bulk pre-fill complete",
      description: `${successCount} succeeded, ${errors.length} failed.`,
      variant: errors.length > 0 ? "destructive" : "default",
    });
  };

  const runBulkGenerate = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    cancelRef.current = false;
    const titles = await resolveBookTitles(ids);
    const errors: BulkOperationProgress["errors"] = [];

    setProgress({
      isRunning: true,
      type: "generate",
      total: ids.length,
      completed: 0,
      currentBookTitle: "",
      errors: [],
    });

    // Process in batches of 2 for concurrency limit
    for (let i = 0; i < ids.length; i += 2) {
      if (cancelRef.current) break;
      const batch = ids.slice(i, i + 2);

      setProgress((p) => ({
        ...p,
        currentBookTitle: batch.map((id) => titles.get(id) || `#${id}`).join(", "),
        completed: i,
      }));

      const results = await Promise.allSettled(
        batch.map((bookId) => generateEmbeddingsForBook(bookId))
      );

      results.forEach((result, idx) => {
        if (result.status === "rejected") {
          const bookId = batch[idx];
          errors.push({
            bookId,
            title: titles.get(bookId) || `Book #${bookId}`,
            error: result.reason instanceof Error ? result.reason.message : "Unknown error",
          });
        }
      });
    }

    setProgress((p) => ({
      ...p,
      isRunning: false,
      completed: cancelRef.current ? p.completed : ids.length,
      currentBookTitle: "",
      errors,
    }));

    invalidateAll();
    const successCount = ids.length - errors.length;
    toast({
      title: "Bulk embedding generation complete",
      description: `${successCount} succeeded, ${errors.length} failed.`,
      variant: errors.length > 0 ? "destructive" : "default",
    });
  };

  const runBulkPrefillAndGenerate = async () => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;

    cancelRef.current = false;
    const titles = await resolveBookTitles(ids);
    const errors: BulkOperationProgress["errors"] = [];

    // Phase 1: Pre-fill all
    setProgress({
      isRunning: true,
      type: "prefill-and-generate",
      total: ids.length * 2, // 2 phases
      completed: 0,
      currentBookTitle: "",
      errors: [],
    });

    for (let i = 0; i < ids.length; i++) {
      if (cancelRef.current) break;
      const bookId = ids[i];
      const title = titles.get(bookId) || `Book #${bookId}`;

      setProgress((p) => ({ ...p, currentBookTitle: `Pre-fill: ${title}`, completed: i }));

      try {
        await prefillAiContextFromChapters(bookId);
      } catch (error) {
        errors.push({ bookId, title, error: `Pre-fill: ${error instanceof Error ? error.message : "Unknown"}` });
      }
    }

    // Phase 2: Generate all (batches of 2)
    for (let i = 0; i < ids.length; i += 2) {
      if (cancelRef.current) break;
      const batch = ids.slice(i, i + 2);

      setProgress((p) => ({
        ...p,
        currentBookTitle: `Generate: ${batch.map((id) => titles.get(id) || `#${id}`).join(", ")}`,
        completed: ids.length + i,
      }));

      const results = await Promise.allSettled(
        batch.map((bookId) => generateEmbeddingsForBook(bookId))
      );

      results.forEach((result, idx) => {
        if (result.status === "rejected") {
          const bookId = batch[idx];
          const existing = errors.find((e) => e.bookId === bookId);
          if (!existing) {
            errors.push({
              bookId,
              title: titles.get(bookId) || `Book #${bookId}`,
              error: `Generate: ${result.reason instanceof Error ? result.reason.message : "Unknown"}`,
            });
          }
        }
      });
    }

    setProgress((p) => ({
      ...p,
      isRunning: false,
      completed: cancelRef.current ? p.completed : ids.length * 2,
      currentBookTitle: "",
      errors,
    }));

    invalidateAll();
    const successCount = ids.length - errors.length;
    toast({
      title: "Bulk pre-fill + generate complete",
      description: `${successCount} succeeded, ${errors.length} failed.`,
      variant: errors.length > 0 ? "destructive" : "default",
    });
  };

  const handleCancel = () => {
    cancelRef.current = true;
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setProgress(defaultProgress);
  };

  return (
    <AdminLayout title="Knowledge Base">
      <div className="space-y-6">
        <KnowledgeBaseStats stats={stats} isLoading={statsLoading} />

        {(selectedIds.size > 0 || progress.isRunning || (progress.completed > 0 && !progress.isRunning)) && (
          <BulkActionToolbar
            selectedCount={selectedIds.size}
            progress={progress}
            onClearSelection={handleClearSelection}
            onBulkPrefill={runBulkPrefill}
            onBulkGenerate={runBulkGenerate}
            onBulkPrefillAndGenerate={runBulkPrefillAndGenerate}
            onCancel={handleCancel}
          />
        )}

        <BookContextTable
          books={booksResult?.books || []}
          totalCount={booksResult?.totalCount || 0}
          page={page}
          pageSize={PAGE_SIZE}
          filters={filters}
          sortField={sortField}
          sortAsc={sortAsc}
          selectedIds={selectedIds}
          categories={categories}
          languages={languages}
          isLoading={booksLoading}
          isOperationRunning={progress.isRunning}
          onPageChange={setPage}
          onFiltersChange={handleFiltersChange}
          onSortChange={handleSortChange}
          onSelectionChange={setSelectedIds}
          onPrefill={handleSinglePrefill}
          onGenerate={handleSingleGenerate}
          onPrefillAndGenerate={handleSinglePrefillAndGenerate}
        />
      </div>
    </AdminLayout>
  );
};

export default AiKnowledgeBase;
